"use client";

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { QueryResult } from '../../components/Alerts.tsx';
import ProtectedRoute from '../../components/ProtectedRoute';
import { toastError } from '../../components/Toast.tsx';
import { useAppSelector } from '../../lib/hooks';
import {
    Notification,
    Periode,
    useGetUserPreferencesQuery,
    useStoreNotificationPreferenceMutation,
    useStoreSignupPreferenceMutation,
    UserPreferences
} from '../../lib/model/kantoordagApi';
import { useGetBuildingsQuery } from '../../lib/model/kantoordag/buildingsStreamingApi.ts';
import { Button, Card, Label, Select, TextInput, ToggleSwitch } from 'flowbite-react';
import { ROOM__AUTOMATIC_ASSIGNMENT, ROOM__DEFAULT_ROOMS, ROOM__NO_ROOM } from "../Constants.tsx";

export default function Preferences() {
    const {t} = useTranslation("preferences");

    const user = useAppSelector(state => state.user.user)

    const preferences = useGetUserPreferencesQuery()
    const [putNotificationPref, notificationResult] = useStoreNotificationPreferenceMutation();
    const [putSignupPref, signupResult] = useStoreSignupPreferenceMutation();
    const gebouwQuery = useGetBuildingsQuery()

    const [building, setBuilding] = useState<string | undefined>(undefined);
    const [room, setRoom] = useState<string | undefined>("NoRoom");
    const [parkingRequested, setParkingRequested] = useState<boolean | undefined>(undefined);
    const [periode, setPeriode] = useState<Periode | undefined>(undefined);

    function onNotificationPrefChange(notification: Notification, checked: boolean) {
        putNotificationPref({
            notification: notification,
            notificationPreferred: {
                preferred: checked
            }
        }).unwrap().then(() => {
            toast.success(t("saving_success"))
            preferences.refetch()
        }).catch(() => {
            toastError({api: notificationResult, message: t("saving_failed")})
        })
    }

    const rooms = useCallback(() => {
        if (building == undefined || gebouwQuery.data == undefined) {
            return ROOM__DEFAULT_ROOMS
        } else {
            const selectedBuilding = gebouwQuery.data.filter(b => b.name == building)[0];
            return selectedBuilding ? selectedBuilding.rooms.map(r => r.name) : ROOM__DEFAULT_ROOMS;
        }
    }, [gebouwQuery.data, building])

    function roomName(roomName: string): string {
        return roomName == ROOM__NO_ROOM || roomName == ROOM__AUTOMATIC_ASSIGNMENT ? t(roomName) : roomName;
    }

    function emptyToUndefined<T>(value: T | undefined): T | undefined{
        if (value == "") {
            return undefined
        }
        return value
    }

    function saveSignup() {
        putSignupPref({
            signupPrefs: {
                building: emptyToUndefined(building),
                room: emptyToUndefined(room),
                parkingRequested: parkingRequested,
                periode: emptyToUndefined(periode)
            }
        }).unwrap().then(() => {
            toast.success(t("saving_success"))
            preferences.refetch()
        }).catch(() => {
            toastError({api: signupResult, message: t("saving_failed")})
        })
    }

    useEffect(() => {
        if (preferences.data) {
            setBuilding(preferences.data.signup?.building ?? undefined)
            setRoom(preferences.data.signup?.room ?? undefined)
            setParkingRequested(preferences.data.signup?.parkingRequested ?? undefined)
            setPeriode(preferences.data.signup?.periode ?? undefined)
        }
    }, [preferences.data])

    return (
        <div>
            <ProtectedRoute authenticated={true}>
                <Card className={"mb-2.5"}>
                    <h1>
                        {t("user_info")}
                    </h1>
                    <div className="grid grid-cols-2">
                        <Label>{t("name")}</Label>
                        <TextInput id="name" type="text" disabled value={user?.displayName ?? ''}/>

                        <Label>{t("email")}</Label>
                        <TextInput id="email" type="text" disabled value={user?.email ?? ''}/>
                    </div>
                </Card>

                <Card>
                    <h1>
                        {t("notifications")}
                    </h1>

                    <QueryResult query={preferences} message={t("loading_failed")}>
                        {
                            preferences.data ?
                                <PreferencesList preferences={preferences.data} onChange={onNotificationPrefChange}/>
                                : null
                        }
                    </QueryResult>
                </Card>

                <Card>
                    <h1>
                        {t("signup")}
                    </h1>

                    <QueryResult query={gebouwQuery} message={t("loading_failed")}>
                        {
                            gebouwQuery.data ?
                                (
                                    <QueryResult query={preferences} message={t("loading_failed")}>
                                        {
                                            preferences.data ?
                                                <div className={"flex flex-col gap-2"}>
                                                    <div className="w-full flex flex-col">
                                                        <Label>{t('building')}</Label>
                                                        <Select value={building}
                                                                onChange={(e) => setBuilding(e.target.value)}
                                                        >
                                                            <option value="">{t('no_preference')}</option>
                                                            {gebouwQuery.data.map((building) => (
                                                                <option key={building.name} value={building.name}>{building.name}</option>
                                                            ))}
                                                        </Select>
                                                    </div>

                                                    <div className="w-full flex flex-col">
                                                        <Label>{t('room')}</Label>
                                                        <Select value={room}
                                                                onChange={(e) => setRoom(e.target.value)}
                                                        >
                                                            {rooms().map((rm) => (
                                                                <option key={rm} value={rm}>{roomName(rm)}</option>
                                                            ))}
                                                        </Select>
                                                    </div>

                                                    <div className="flex max-w-md flex-col items-start gap-4">
                                                        <ToggleSwitch
                                                            checked={parkingRequested ?? false}
                                                            onChange={(checked) => setParkingRequested(checked)}
                                                            label={t('requestParking')}
                                                        />
                                                    </div>

                                                    <div className="w-full flex flex-col">
                                                        <Label htmlFor={"periode"}>{t('periode')}</Label>
                                                        <Select value={periode} id={"periode"}
                                                                onChange={(e) => setPeriode(e.target.value as Periode)}
                                                        >
                                                            <option value="">{t('no_preference')}</option>
                                                            <option value={Periode.HeleDag}>{t('wholeDay')}</option>
                                                            <option value={Periode.DagDeel}>{t('partDay')}</option>
                                                        </Select>
                                                    </div>

                                                    <Button color={"primary"} onClick={saveSignup}>{t("save")}</Button>
                                                </div>
                                                : null
                                        }
                                    </QueryResult>
                                )
                                : null
                        }
                    </QueryResult>

                </Card>
            </ProtectedRoute>
        </div>
    )

}

function PreferencesList(
    {
        preferences, onChange
    } : {
        preferences: UserPreferences,
        onChange: (notifaction: Notification, checked: boolean) => void
    }) {

    const {t} = useTranslation("preferences");

    function isChecked(notification: Notification) {
        return preferences.notifications[notification]
    }

    function checkboxChanged(checked: boolean, notification: Notification) {
        onChange(notification, checked)
    }

    return (
        <div className={"flex flex-col gap-2"}>
            {
                Object.values(Notification).map(notification =>
                    (
                        <ToggleSwitch checked={isChecked(notification)}
                                      onChange={c => checkboxChanged(c, notification)}
                                      label={t(notification)}
                                      key={notification}
                        />
                    )
                )
            }
        </div>
    )

}